import React, { useState, useMemo } from 'react';

interface Movement {
  type: string;
  directions: Array<{
    rowDelta: number;
    colDelta: number;
  }>;
  maxDistance: number | 'unlimited';
}

interface PieceMovementDiagramProps {
  pieceName: string;
  pieceAbbreviation: string;
  pieceClass: string;
  movements: Movement[];
  onClose: () => void;
}

const PieceMovementDiagram: React.FC<PieceMovementDiagramProps> = ({
  pieceName,
  pieceAbbreviation,
  pieceClass,
  movements,
  onClose
}) => {
  const [showDebug, setShowDebug] = useState(false);
  
  const getClassColor = (pieceClass: string): string => {
    const colors: Record<string, string> = {
      ruling: '#4169E1',
      architectural: '#8B4513',
      religious: '#9370DB',
      military: '#DC143C',
      weaponry: '#708090',
      special: '#20B2AA'
    };
    return colors[pieceClass] || '#4A90E2';
  };

  const pieceImagePath = `/pieces/${pieceAbbreviation.toLowerCase()}.png`;
  const boardSize = 12;
  const centerRow = 5;
  const centerCol = 5;
  
  // Determine movement category based on directions
  const getMovementCategory = (directions: Array<{rowDelta: number, colDelta: number}>): string => {
    if (!directions || directions.length === 0) return 'unknown';
    
    const first = directions[0];
    const isOrthogonal = first.rowDelta === 0 || first.colDelta === 0;
    const isDiagonal = Math.abs(first.rowDelta) === Math.abs(first.colDelta) && first.rowDelta !== 0;
    const isLType = Math.abs(first.rowDelta) !== Math.abs(first.colDelta) && first.rowDelta !== 0 && first.colDelta !== 0;
    
    if (isOrthogonal) return 'orthogonal';
    if (isDiagonal) return 'diagonal';
    if (isLType) return 'ltype';
    return 'unknown';
  };
  
  const { reachableSquares, debugInfo } = useMemo(() => {
    const reachable = new Map<string, string>();
    const debug: string[] = [];
    
    debug.push('=== PROCESSING MOVEMENTS ===');
    debug.push(`Number of movements: ${movements?.length || 0}`);
    
    if (!movements || movements.length === 0) {
      debug.push('ERROR: No movements provided!');
      return { reachableSquares: reachable, debugInfo: debug };
    }
    
    movements.forEach((movement, index) => {
      debug.push(`\n--- Movement ${index}: ${movement.type} ---`);
      debug.push(`Directions count: ${movement.directions.length}`);
      debug.push(`Max distance: ${movement.maxDistance}`);
      
      const category = getMovementCategory(movement.directions);
      debug.push(`Detected category: ${category}`);
      
      const maxDist = movement.maxDistance === 'unlimited' ? boardSize : Number(movement.maxDistance);
      let addedCount = 0;
      
      // Process each direction
      movement.directions.forEach((dir, dirIdx) => {
        debug.push(`  Direction ${dirIdx}: row=${dir.rowDelta}, col=${dir.colDelta}`);
        
        // For slide movements, go up to maxDistance
        // For jump movements, only go to the exact position (maxDistance is usually 1)
        const steps = movement.type === 'slide' ? maxDist : 1;
        
        for (let step = 1; step <= steps; step++) {
          const newRow = centerRow + (dir.rowDelta * step);
          const newCol = centerCol + (dir.colDelta * step);
          
          if (newRow >= 0 && newRow < boardSize && newCol >= 0 && newCol < boardSize) {
            reachable.set(`${newRow},${newCol}`, category);
            addedCount++;
          }
        }
      });
      
      debug.push(`Added ${addedCount} squares for this movement`);
    });
    
    debug.push(`\n=== SUMMARY ===`);
    debug.push(`Total reachable squares: ${reachable.size}`);
    debug.push(`Sample squares: ${Array.from(reachable.entries()).slice(0, 10).map(([k, v]) => `${k}:${v}`).join(', ')}`);
    
    return { reachableSquares: reachable, debugInfo: debug };
  }, [movements, boardSize, centerRow, centerCol]);
  
  const getSquareType = (row: number, col: number): 'piece' | 'reachable' | 'normal' => {
    if (row === centerRow && col === centerCol) return 'piece';
    if (reachableSquares.has(`${row},${col}`)) return 'reachable';
    return 'normal';
  };

  const getMovementSymbol = (row: number, col: number): string => {
    const key = `${row},${col}`;
    const movementType = reachableSquares.get(key);

    if (!movementType) return '';

    return '●';
  };

  const getMovementColor = (row: number, col: number): string => {
    const key = `${row},${col}`;
    const movementType = reachableSquares.get(key);

    if (movementType === 'orthogonal') return '#0000FF'; // Blue
    if (movementType === 'diagonal') return '#00CC00';   // Light Green
    if (movementType === 'ltype') return '#FF0000';      // Red

    return '#333';
  };

  const columnLabels = 'abcdefghijkl'.split('');
  const primaryColor = getClassColor(pieceClass);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000,
      padding: '20px',
      overflow: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '15px',
        padding: '30px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'visible',
        boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
      }}>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img
              src={pieceImagePath}
              alt={pieceName}
              style={{
                width: '113px',
                height: '113px',
                objectFit: 'contain'
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <div>
              <h2 style={{ 
                margin: 0, 
                color: '#333',
                fontSize: '24px'
              }}>
                {pieceName} ({pieceAbbreviation}) - Movement Diagram
              </h2>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Class: {pieceClass.charAt(0).toUpperCase() + pieceClass.slice(1)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              color: '#666',
              padding: '5px 10px',
              borderRadius: '5px',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>



        {/* Movement Board */}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          {/* Board */}
          <div style={{ display: 'flex' }}>
            {/* Row labels left */}
            <div style={{ display: 'flex', flexDirection: 'column', marginRight: '5px' }}>
              {Array.from({ length: boardSize }, (_, i) => boardSize - i).map(num => (
                <div key={num} style={{
                  width: '25px',
                  height: '53px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}>
                  {String(num).padStart(2, '0')}
                </div>
              ))}
            </div>

            {/* Chess board */}
            <div>
              {Array.from({ length: boardSize }, (_, row) => (
                <div key={row} style={{ display: 'flex' }}>
                  {Array.from({ length: boardSize }, (_, col) => {
                    const squareType = getSquareType(row, col);
                    const isLightSquare = (row + col) % 2 === 0;
                    const symbol = getMovementSymbol(row, col);
                    
                    let bgColor = isLightSquare ? '#FFFFFF' : '#A9A9A9';
                    if (squareType === 'piece') {
                      bgColor = '#87CEEB'; // Light blue background for piece
                    }
                    
                    return (
                      <div
                        key={`${row},${col}`}
                        style={{
                          width: '53px',
                          height: '53px',
                          backgroundColor: bgColor,
                          border: '1px solid #999',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: squareType === 'piece' ? '39px' : '47px',
                          fontWeight: 'bold',
                          color: squareType === 'piece' ? 'white' : getMovementColor(row, col),
                          position: 'relative'
                        }}
                      >
                        {squareType === 'piece' ? (
                          <img 
                            src={pieceImagePath}
                            alt={pieceAbbreviation}
                            style={{
                              width: '62px',
                              height: '62px',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              // Fallback to text if image fails to load
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.textContent = pieceAbbreviation;
                            }}
                          />
                        ) : symbol}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          {/* Column labels bottom */}
          <div style={{ display: 'flex', marginLeft: '30px', marginTop: '5px' }}>
            {columnLabels.map((label, i) => (
              <div key={i} style={{
                width: '53px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '18px',
                fontWeight: 'bold'
              }}>
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Legend */}
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px'
        }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '24px' }}>Legend</h3>
          <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '36px', fontWeight: 'bold', color: '#0000FF' }}>●</span>
              <span style={{ fontSize: '20px' }}>Orthogonal (Castle-like)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '36px', fontWeight: 'bold', color: '#00CC00' }}>●</span>
              <span style={{ fontSize: '20px' }}>Diagonal (Bishop-like)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '36px', fontWeight: 'bold', color: '#FF0000' }}>●</span>
              <span style={{ fontSize: '20px' }}>L-Type (Knight-like)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PieceMovementDiagram;